import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { useAuthStore } from '../store/auth'
import type { ApiResponse, ApiError } from '../types/api'

// API 클라이언트 인스턴스 생성
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // 요청 인터셉터: 인증 토큰 추가
  client.interceptors.request.use(
    (config) => {
      const { token } = useAuthStore.getState()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // 응답 인터셉터: 에러 처리 및 토큰 갱신
  client.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
      return response
    },
    (error) => {
      const { response } = error

      if (response?.status === 401) {
        // 인증 실패시 로그아웃
        const { logout } = useAuthStore.getState()
        logout()
        window.location.href = '/login'
      }

      // API 에러 형식으로 변환
      const apiError: ApiError = {
        message: response?.data?.message || error.message || '알 수 없는 오류가 발생했습니다',
        code: response?.data?.code || response?.status?.toString(),
        details: response?.data?.details,
      }

      return Promise.reject(apiError)
    }
  )

  return client
}

// API 클라이언트 인스턴스
export const apiClient = createApiClient()

// API 헬퍼 함수들
export const api = {
  get: <T>(url: string, params?: any) =>
    apiClient.get<ApiResponse<T>>(url, { params }).then((res) => res.data.data),

  post: <T>(url: string, data?: any) =>
    apiClient.post<ApiResponse<T>>(url, data).then((res) => res.data.data),

  put: <T>(url: string, data?: any) =>
    apiClient.put<ApiResponse<T>>(url, data).then((res) => res.data.data),

  patch: <T>(url: string, data?: any) =>
    apiClient.patch<ApiResponse<T>>(url, data).then((res) => res.data.data),

  delete: <T>(url: string) =>
    apiClient.delete<ApiResponse<T>>(url).then((res) => res.data.data),

  upload: <T>(url: string, formData: FormData) =>
    apiClient
      .post<ApiResponse<T>>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((res) => res.data.data),
}

export default apiClient